import React, { useState, useEffect } from 'react';
import { X, Save, Plus } from 'lucide-react';
import { SchoolStudent, SchoolStudentCreateRequest } from '../types';
import toast from 'react-hot-toast';

const GRADE_OPTIONS = Array.from({ length: 12 }, (_, index) => String(index + 1));

interface CreateSchoolStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  schoolId?: string;
  schoolName?: string;
  editingStudent?: SchoolStudent | null;
  onSave: (data: SchoolStudentCreateRequest) => void | Promise<void>;
  isLoading?: boolean;
}

const CreateSchoolStudentModal: React.FC<CreateSchoolStudentModalProps> = ({
  isOpen,
  onClose,
  schoolId,
  schoolName,
  editingStudent,
  onSave,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<SchoolStudentCreateRequest>({
    name: '',
    email: '',
    phone: '',
    schoolId: schoolId || '',
    schoolName: schoolName || '',
    code: '',
    studentId: '',
    dateOfBirth: '',
    gender: 'male',
    address: '',
    parentName: '',
    parentPhone: '',
    parentEmail: '',
    emergencyContact: '',
    emergencyPhone: '',
    grade: '',
    classId: '',
    schoolYear: '',
    enrollmentDate: '',
    academicLevel: '',
    subjects: [],
    metadata: {}
  });

  const [newSubject, setNewSubject] = useState('');

  // Reset form when modal opens/closes or editing student changes
  useEffect(() => {
    if (isOpen) {
      if (editingStudent) {
        setFormData({
          name: editingStudent.name || '',
          email: editingStudent.email || '',
          phone: editingStudent.phone || '',
          schoolId: editingStudent.schoolId || schoolId || '',
          schoolName: editingStudent.schoolName || schoolName || '',
          code: editingStudent.code || '',
          studentId: editingStudent.studentId || '',
          dateOfBirth: editingStudent.dateOfBirth || '',
          gender: (editingStudent.gender || '') as SchoolStudentCreateRequest['gender'],
          address: editingStudent.address || '',
          parentName: editingStudent.parentName || '',
          parentPhone: editingStudent.parentPhone || '',
          parentEmail: editingStudent.parentEmail || '',
          emergencyContact: editingStudent.emergencyContact || '',
          emergencyPhone: editingStudent.emergencyPhone || '',
          grade: editingStudent.grade || '',
          classId: editingStudent.classId || '',
          schoolYear: editingStudent.schoolYear || '',
          enrollmentDate: editingStudent.enrollmentDate || '',
          academicLevel: editingStudent.academicLevel || '',
          subjects: editingStudent.subjects || [],
          metadata: editingStudent.metadata || {}
        });
      } else {
        setFormData({
          name: '',
          email: '',
          phone: '',
          schoolId: schoolId || '',
          schoolName: schoolName || '',
          code: '',
          studentId: '',
          dateOfBirth: '',
          gender: 'male',
          address: '',
          parentName: '',
          parentPhone: '',
          parentEmail: '',
          emergencyContact: '',
          emergencyPhone: '',
          grade: '',
          classId: '',
          schoolYear: '',
          enrollmentDate: '',
          academicLevel: '',
          subjects: [],
          metadata: {}
        });
      }
      setNewSubject('');
    }
  }, [isOpen, editingStudent, schoolId, schoolName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || (!editingStudent && (!formData.email || !formData.phone))) {
      toast.error('Vui lòng điền đầy đủ thông tin bắt buộc');
      return;
    }

    try {
      await onSave(formData);
    } catch {
      // Nơi gọi đã báo lỗi bằng toast; giữ modal mở để người dùng sửa lại
    }
  };

  const addSubject = () => {
    if (newSubject.trim() && !formData.subjects?.includes(newSubject.trim())) {
      setFormData(prev => ({
        ...prev,
        subjects: [...(prev.subjects || []), newSubject.trim()]
      }));
      setNewSubject('');
    }
  };

  const removeSubject = (subjectToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects?.filter(subject => subject !== subjectToRemove) || []
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingStudent ? 'Chỉnh sửa học sinh' : 'Thêm học sinh mới'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Thông tin cơ bản</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Họ tên <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Nhập họ tên"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email {!editingStudent && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="email@example.com"
                    required={!editingStudent}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Số điện thoại {!editingStudent && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="0123456789"
                    required={!editingStudent}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mã học sinh</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="HS001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mã số học sinh</label>
                  <input
                    type="text"
                    value={formData.studentId}
                    onChange={(e) => setFormData(prev => ({ ...prev, studentId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="2024001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ngày sinh</label>
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => setFormData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Giới tính</label>
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    {editingStudent && <option value="">-- Chưa chọn --</option>}
                    <option value="male">Nam</option>
                    <option value="female">Nữ</option>
                    <option value="other">Khác</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Địa chỉ</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Địa chỉ nhà"
                  />
                </div>
              </div>
            </div>

            {/* Parent Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Thông tin phụ huynh</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Họ tên phụ huynh</label>
                  <input
                    type="text"
                    value={formData.parentName}
                    onChange={(e) => setFormData(prev => ({ ...prev, parentName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Họ tên phụ huynh"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Số điện thoại phụ huynh</label>
                  <input
                    type="tel"
                    value={formData.parentPhone}
                    onChange={(e) => setFormData(prev => ({ ...prev, parentPhone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="0123456789"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email phụ huynh</label>
                  <input
                    type="email"
                    value={formData.parentEmail}
                    onChange={(e) => setFormData(prev => ({ ...prev, parentEmail: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="parent@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Người liên hệ khẩn cấp</label>
                  <input
                    type="text"
                    value={formData.emergencyContact}
                    onChange={(e) => setFormData(prev => ({ ...prev, emergencyContact: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Họ tên người liên hệ"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Số điện thoại khẩn cấp</label>
                  <input
                    type="tel"
                    value={formData.emergencyPhone}
                    onChange={(e) => setFormData(prev => ({ ...prev, emergencyPhone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="0123456789"
                  />
                </div>
              </div>
            </div>

            {/* Academic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Thông tin học tập</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="student-grade" className="block text-sm font-medium text-gray-700 mb-1">Khối lớp</label>
                  <select
                    id="student-grade"
                    value={formData.grade}
                    onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Chọn khối lớp</option>
                    {/* Khối nhập từ Excel có thể là "K1", "Khối 10"... không khớp 1-12; giữ lại
                        để học sinh đó vẫn sửa được thay vì bị select bỏ trống rồi chặn khi lưu */}
                    {formData.grade && !GRADE_OPTIONS.includes(formData.grade) && (
                      <option value={formData.grade}>{formData.grade}</option>
                    )}
                    {GRADE_OPTIONS.map((grade) => (
                      <option key={grade} value={grade}>Lớp {grade}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Năm học</label>
                  <input
                    type="text"
                    value={formData.schoolYear}
                    onChange={(e) => setFormData(prev => ({ ...prev, schoolYear: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="2024-2025"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ngày nhập học</label>
                  <input
                    type="date"
                    value={formData.enrollmentDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, enrollmentDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trình độ học vấn</label>
                  <select
                    value={formData.academicLevel}
                    onChange={(e) => setFormData(prev => ({ ...prev, academicLevel: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Chọn trình độ</option>
                    <option value="elementary">Tiểu học</option>
                    <option value="middle">Trung học cơ sở</option>
                    <option value="high">Trung học phổ thông</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Subjects */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Môn học</h3>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Thêm môn học..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubject())}
                />
                <button
                  type="button"
                  onClick={addSubject}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.subjects?.map((subject) => (
                  <span key={subject} className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                    {subject}
                    <button
                      type="button"
                      onClick={() => removeSubject(subject)}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isLoading ? 'Đang lưu...' : (editingStudent ? 'Cập nhật' : 'Thêm mới')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSchoolStudentModal;
